import DiscoverPosts from '@/components/DiscoverPosts';
import FollowedPosts from '@/components/FollowedPosts';
import GolfIcon from '@/components/GolfIcon';
import NotificationsRequest from '@/components/NotificationsRequest';
import { GolfAvatar } from '@/components/ui/GolfAvatar';
import { useUserProfile } from '@/context/UserProfileContext';
import { useToast } from '@/hooks/use-toast';
import { postEventService } from '@/services/PostEventService';
import { api } from '@/utils/api';
import { mediumImpact } from '@/utils/capacitorUtils';
import { FirebaseMessaging } from '@capacitor-firebase/messaging';
import { Capacitor } from '@capacitor/core';
import { IonButton, IonButtons, IonContent, IonFab, IonFabButton, IonHeader, IonIcon, IonModal, IonPage, IonRefresher, IonRefresherContent, IonRouterLink, IonSegment, IonSegmentButton, IonSegmentContent, IonSegmentView, IonToolbar, RefresherEventDetail } from '@ionic/react';
import { formatDistanceToNow } from 'date-fns';
import { t } from 'i18next';
import { add, checkmarkCircle } from 'ionicons/icons';
import { useCallback, useEffect, useRef, useState } from 'react';
import { supabase } from '@/utils/supabase';

const Discover: React.FC = () => {
  document.title = "CapGolf";

  // State
  const [showNotificationsRequest, setShowNotificationsRequest] = useState(false);
  const [activeTab, setActiveTab] = useState<'discover' | 'followed'>('followed');
  const [hasUnreadNotifications, setHasUnreadNotifications] = useState(false);
  const pageRef = useRef<HTMLElement>(null);

  const { toast } = useToast();

  const { profile, membership } = useUserProfile();
  const isInitialMount = useRef(true);

  // Add a new post to feed
  const addPostToFeed = useCallback((newPost: any) => {
    console.log('New post added:', newPost);
    // The post will be passed to the FollowedPosts component
  }, []);

  const handleRefresh = (event: CustomEvent<RefresherEventDetail>) => {
    // The refresh will be handled by the individual components
    mediumImpact();
    setTimeout(() => {
      event.detail.complete();
    }, 500);
  };

  // Handle post deletion
  const handleDeletePost = useCallback(
    async (postId: number) => {
      try {
        const response = await api.delete(`/posts/${postId}`);

        if (!response.data) {
          throw new Error('Failed to delete post');
        }

        // Show success toast
        toast({
          description: (
            <span>
              <IonIcon icon={checkmarkCircle} style={{ color: 'green', marginRight: '8px' }} />
              <span>{t('post_deleted')}</span>
            </span>
          ),
        });

        // Emit post deleted event to update all feeds
        postEventService.emitPostDeleted(postId);

        return response.data;
      } catch (error) {
        console.error('Error deleting post:', error);
        return null;
      }
    },
    [toast, t],
  );

  // Check notification permissions when component mounts
  useEffect(() => {
    const checkNotificationPermissions = async () => {
      if (Capacitor.isNativePlatform() && Capacitor.isPluginAvailable("FirebaseMessaging")) {
        try {
          const permissionStatus = await FirebaseMessaging.checkPermissions();

          // Show notification request if status is 'prompt' or 'prompt-with-rationale'
          if (permissionStatus.receive === 'prompt' || permissionStatus.receive === 'prompt-with-rationale') {
            setShowNotificationsRequest(true);
          }
        } catch (error) {
          console.error('Error checking notification permissions:', error);
        }
      }
    };

    checkNotificationPermissions();
  }, []);

  // Check for unread notifications
  useEffect(() => {
    if (!profile?.userId) return;

    const checkUnreadNotifications = async () => {
      try {
        const { count, error } = await supabase
          .from('notifications')
          .select('id', { count: 'exact', head: true })
          .eq('user_id', profile.userId)
          .eq('read', false);

        if (error) throw error;

        // If count is greater than 0, there are unread notifications
        setHasUnreadNotifications(count !== null && count > 0);
      } catch (error) {
        console.error('Error checking unread notifications:', error);
      }
    };

    // Initial check
    checkUnreadNotifications();

    // Set up realtime subscription for new notifications
    const notificationsChannel = supabase
      .channel('unread-notifications')
      .on(
        'postgres_changes',
        {
          event: '*', // Listen for all events (INSERT, UPDATE, DELETE)
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${profile.userId}`,
        },
        () => {
          // When any notification changes, recheck unread count
          checkUnreadNotifications();
        }
      )
      .subscribe();

    return () => {
      // Clean up subscription
      supabase.removeChannel(notificationsChannel);
    };
  }, [profile?.userId]);

  useEffect(() => {
    if (isInitialMount.current && profile) {
      isInitialMount.current = false;
      // Components will handle their own data loading
    }
  }, [profile]);

  return (
    <IonPage ref={pageRef}>
      <IonContent>
        <div className="mx-auto max-w-lg space-y-2">
          <IonHeader className="ion-no-border">
            <IonToolbar className="md:hidden p-1">
              <IonButtons slot="start">
                <IonRouterLink routerDirection='forward' routerLink={`/menu`}>
                  <GolfAvatar imageUrl={profile?.imageUrl} name={profile?.name} size={40} />
                </IonRouterLink>
                <div className="text-foreground ml-2 flex flex-col">
                  <span>{profile?.username}</span>
                  {membership?.name && <span className="text-xs text-gray-500">{membership.name}</span>}
                </div>
              </IonButtons>
              <IonButtons slot="end">
                <IonButton routerDirection='forward' routerLink='/messages'>
                  <GolfIcon name="message" className='size-7 dark:invert' />
                </IonButton>
                <IonButton routerDirection='forward' routerLink='/notifications'>
                  <div className="relative">
                    <GolfIcon name="notification" className='size-7 dark:invert' />
                    {/* Has notification indicator - only shown when there are unread notifications */}
                    {hasUnreadNotifications && (
                      <div
                        className='absolute -top-px -right-px size-3 items-center justify-center rounded-full text-white bg-primary text-xs'
                      ></div>
                    )}
                  </div>
                </IonButton>
              </IonButtons>
            </IonToolbar>
          </IonHeader>

          {/* Tabs */}
          <IonSegment mode="md" className="discover border-b md:pt-4" value={activeTab} onIonChange={e => setActiveTab(e.detail.value as 'discover' | 'followed')}>
            <IonSegmentButton className="discover" contentId="followed" value="followed">
              {t('subscriptions')}
            </IonSegmentButton>
            <IonSegmentButton className="discover" contentId="discover" value="discover">
              {t('discover')}
            </IonSegmentButton>
          </IonSegment>

          <IonRefresher color='destructive' slot="fixed" onIonRefresh={handleRefresh}>
            <IonRefresherContent></IonRefresherContent>
          </IonRefresher>

          {/* Swipeable Content */}
          <IonSegmentView>
            {/* Followed Tab */}
            <IonSegmentContent id="followed">
              {profile && (
                <FollowedPosts
                  profile={profile}
                  handleDeletePost={handleDeletePost}
                  pageRef={pageRef}
                  addPostToFeed={addPostToFeed}
                />
              )}
            </IonSegmentContent>

            {/* Discover Tab */}
            <IonSegmentContent id="discover">
              {profile && (
                <DiscoverPosts
                  profile={profile}
                  handleDeletePost={handleDeletePost}
                />
              )}
            </IonSegmentContent>
          </IonSegmentView>
        </div>

        {/* Floating action button for mobile */}
        <IonFab vertical="bottom" horizontal="end" slot="fixed">
          <IonFabButton onClick={() => postEventService.emit('openCreatePostModal', {})}>
            <IonIcon icon={add} />
          </IonFabButton>
        </IonFab>

        {/* Notifications Permission Request Modal */}
        <IonModal isOpen={showNotificationsRequest}>
          <NotificationsRequest
            onConfirm={() => setShowNotificationsRequest(false)}
            onCancel={() => setShowNotificationsRequest(false)}
          />
        </IonModal>
      </IonContent >
    </IonPage >
  );
};

export default Discover;
